import api from '@/services/api'
import { normalizeFinanceRequestDetail, type FinanceRequestDetail } from '@/services/financeService'
import { mapDealFromApi } from '@/adapters/deals'
import type { Deal } from '@/types'
import {
  buildContractDataForm,
  buildEmpresaContractPayload,
  type ContractDataForm,
} from '@/components/Deals/DealDetailPanel/contractDataUtils'
import { dateInputToIso } from '@/components/Deals/DealDetailPanel/legalDataUtils'

export type ContractDataConflict = {
  field: 'cnpj' | 'responsavelAssinaturaEmail' | 'emailFinanceiro' | 'telefone'
  message: string
  source: 'empresa' | 'lead'
  referenceId?: string
  referenceName?: string
}

export async function resolveDealForContractData(deal: Deal): Promise<Deal> {
  if (deal.empresaId) return deal

  try {
    const { data } = await api.get(`/deals/${deal.id}`)
    return mapDealFromApi(data.data ?? data)
  } catch {
    return deal
  }
}

export async function loadContractDataFormForDeal(deal: Deal): Promise<ContractDataForm> {
  const resolved = await resolveDealForContractData(deal)

  if (!resolved.empresaId) {
    return buildContractDataForm(resolved)
  }

  try {
    const { data } = await api.get(`/empresas/${resolved.empresaId}`)
    const empresa = data.data ?? data
    return buildContractDataForm(resolved, empresa)
  } catch {
    return buildContractDataForm(resolved)
  }
}

export async function resolveEmpresaIdForDeal(deal: Deal): Promise<string | null> {
  const resolved = await resolveDealForContractData(deal)
  return resolved.empresaId || null
}

export async function checkContractDataConflictsForDeal(
  deal: Deal,
  form: ContractDataForm,
  empresaId?: string | null,
): Promise<ContractDataConflict[]> {
  const excludeEmpresaId = empresaId ?? (await resolveEmpresaIdForDeal(deal))
  const { data } = await api.get('/empresas/contract-conflicts', {
    params: {
      excludeEmpresaId: excludeEmpresaId ?? undefined,
      cnpj: form.cnpj,
      responsavelAssinaturaEmail: form.responsavelAssinaturaEmail,
      emailFinanceiro: form.emailFinanceiro,
      telefone: form.telefone,
    },
  })

  return data.conflicts ?? data.data?.conflicts ?? []
}

export async function getContractRequestForDeal(dealId: string) {
  const { data } = await api.get(`/finance/deals/${dealId}/contract-request`)
  const raw = (data.data ?? data) as FinanceRequestDetail | null
  return raw ? normalizeFinanceRequestDetail(raw) : null
}

export async function persistContractDataForDeal(
  deal: Deal,
  form: ContractDataForm,
): Promise<{ empresaId: string | null; deal: Deal }> {
  const resolved = await resolveDealForContractData(deal)
  const empresaId = resolved.empresaId

  if (!empresaId) {
    throw new Error('Deal sem empresa vinculada')
  }

  await api.patch(`/empresas/${empresaId}`, buildEmpresaContractPayload(form))
  const contractStartDate = dateInputToIso(form.contractStartDate)
  const firstPaymentDate = dateInputToIso(form.firstPaymentDate)

  const deliveryMode =
    form.contractDeliveryMode === 'pdf' || form.contractDeliveryMode === 'link'
      ? form.contractDeliveryMode
      : undefined

  await api.patch(`/deals/${resolved.id}`, {
    contractDeliveryMode: deliveryMode,
    contractStartDate: contractStartDate ?? undefined,
    firstPaymentDate: firstPaymentDate ?? undefined,
  })

  return {
    empresaId,
    deal: {
      ...resolved,
      contractDeliveryMode: deliveryMode,
      contractStartDate: contractStartDate ?? undefined,
      contractStart: contractStartDate ?? undefined,
      firstPaymentDate: firstPaymentDate ?? undefined,
      representanteLegal: form.responsavelAssinaturaNome,
      emailFinanceiro: form.emailFinanceiro,
      contatoNome: form.contatoNome,
      telefone: form.telefone,
      email: form.responsavelAssinaturaEmail,
    },
  }
}
